import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Pencil, Plus, Search, Trash2, UserRoundCheck, X } from "lucide-react";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../../App";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";

interface Signatory {
  id: number;
  name: string;
  mobile: string;
}

const emptyForm = { name: "", mobile: "" };

export const SignatoriesPage: React.FC = () => {
  const { authToken = "", id = "" } = useSelector((state: any) => state.loginData || {});
  const [signatories, setSignatories] = useState<Signatory[]>([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchSignatories = useCallback(async () => {
    if (!id || !authToken) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}account/${id}/quotation-signatories`, {
        params: { firstResults: 0, maxResults: 1000, search },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setSignatories(response.data.signatories || response.data.data || []);
    } catch {
      toast.error("Failed to load signatories");
    } finally {
      setLoading(false);
    }
  }, [authToken, id, search]);

  useEffect(() => {
    const timer = window.setTimeout(fetchSignatories, 250);
    return () => window.clearTimeout(timer);
  }, [fetchSignatories]);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const name = form.name.trim();
    const mobile = form.mobile.trim();
    if (!name || !mobile) {
      toast.error("Name and mobile number are required");
      return;
    }
    setSaving(true);
    try {
      if (editingId !== null) {
        await axios.put(
          `${API_BASE_URL}account/${id}/quotation-signatory/update`,
          { id: editingId, name, mobile },
          { headers: { Authorization: `Bearer ${authToken}` } },
        );
        toast.success("Signatory updated");
      } else {
        await axios.post(
          `${API_BASE_URL}account/${id}/quotation-signatory/create`,
          { name, mobile },
          { headers: { Authorization: `Bearer ${authToken}` } },
        );
        toast.success("Signatory added");
      }
      resetForm();
      await fetchSignatories();
    } catch (error) {
      const message = axios.isAxiosError(error) && error.response?.data?.message
        ? String(error.response.data.message)
        : "Unable to save signatory";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (signatory: Signatory) => {
    if (!window.confirm(`Delete signatory "${signatory.name}"?`)) return;
    try {
      await axios.delete(
        `${API_BASE_URL}account/${id}/quotation-signatory/${signatory.id}/delete`,
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      toast.success("Signatory deleted");
      await fetchSignatories();
    } catch {
      toast.error("Unable to delete signatory");
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <UserRoundCheck className="h-7 w-7 text-[var(--sidebar-primary)]" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Quotation Signatories</h1>
            <p className="text-sm text-gray-500">Manage the names and mobile numbers available on quotations.</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
          <Input
            value={form.name}
            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
            placeholder="Signatory name"
            disabled={saving}
          />
          <Input
            value={form.mobile}
            onChange={(event) => setForm((current) => ({ ...current, mobile: event.target.value }))}
            placeholder="Mobile number"
            inputMode="tel"
            disabled={saving}
          />
          <div className="flex gap-2">
            <Button type="submit" disabled={saving} className="btn-primary flex-1 md:flex-none">
              {editingId === null ? <Plus className="mr-1 h-4 w-4" /> : <Pencil className="mr-1 h-4 w-4" />}
              {saving ? "Saving…" : editingId === null ? "Add" : "Update"}
            </Button>
            {editingId !== null && (
              <Button type="button" variant="outline" onClick={resetForm} disabled={saving}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </form>

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by name or mobile…"
          className="pl-9"
        />
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <p className="p-8 text-center text-sm text-gray-500">Loading signatories…</p>
        ) : signatories.length === 0 ? (
          <p className="p-8 text-center text-sm text-gray-500">No signatories found.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-gray-200 bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Name</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Mobile</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {signatories.map((signatory) => (
                <tr key={signatory.id} className="border-b border-gray-100 last:border-0">
                  <td className="px-4 py-3 font-semibold text-gray-900">{signatory.name}</td>
                  <td className="px-4 py-3 text-gray-700">{signatory.mobile}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingId(signatory.id);
                          setForm({ name: signatory.name, mobile: signatory.mobile });
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-200 text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(signatory)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default SignatoriesPage;
